import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useNotify } from '@routes-plan/ui';
import { Country } from '../../../models/country';
import { nowOperationalDateTimeString } from '../../../utils/operationalDateTime';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { apiError } from '../shared/AdminUi';
import OfficeForm, { BusinessModelOption, OfficeFormErrors, OfficeFormValues, validateOffice } from './OfficeForm';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const EMPTY: OfficeFormValues = {
  name: '',
  phone: '',
  address: '',
  city: '',
  countryId: '',
  businessModelId: '',
  // ¿Las rutas de la sede comparten los clientes? (por defecto no, como siempre)
  shareClients: false,
};

const OfficeNew: React.FC<ShowAlerts> = () => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { canCreate } = useResourcePermissions('/admin/offices');
  const [values, setValues] = useState<OfficeFormValues>(EMPTY);
  const [errors, setErrors] = useState<OfficeFormErrors>({});
  const [countries, setCountries] = useState<Country[]>([]);
  const [businessModels, setBusinessModels] = useState<BusinessModelOption[]>([]);
  const [saving, setSaving] = useState(false);

  setPageTitle('offices.newPageTitle');
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: countryData } = await axios.get('typecountry');
        setCountries(Array.isArray(countryData?.data) ? countryData.data : []);
        const { data: modelsData } = await axios.get('business-models');
        setBusinessModels(Array.isArray(modelsData?.data) ? modelsData.data : []);
      } catch (error) {
        notify.error(t('common.loadError'));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!canCreate) return;
    const found = validateOffice(values, t('common.required'), t('offices.officeTypeRequiredHint'));
    setErrors(found);
    if (Object.keys(found).length) {
      notify.warning(t('offices.missingFields'));
      return;
    }
    setSaving(true);
    try {
      await axios.post('/offices', {
        currentDate: nowOperationalDateTimeString(),
        name: values.name,
        phone: values.phone,
        address: values.address,
        city: values.city,
        manager: '',
        countryId: values.countryId ? Number(values.countryId) : '',
        businessModelId: Number(values.businessModelId),
        shareClients: values.shareClients,
      });
      notify.success(t('offices.createdOk'));
      navigate('/admin/offices');
    } catch (error) {
      notify.error(apiError(error, t('offices.createError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <OfficeForm
      mode="new"
      values={values}
      errors={errors}
      countries={countries}
      businessModels={businessModels}
      onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
      onSubmit={submit}
      saving={saving}
      allowed={canCreate}
      dirty
    />
  );
};

export default OfficeNew;
